/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/layouts/ManagerLayout.tsx
 * PURPOSE: Manager shell layout — sidebar 240px + topbar ☰ + content (SC-010-HUB-BM shell)
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-29
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager shell (Quản lý chi nhánh / Trưởng ca / Bếp trưởng)
 * - Impact: UX Critical — shell chung cho /manager/* (hub, schedule, attendance, staff, profile)
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Layout Component)
 * - Pattern: React Router Outlet layout + role guard (mirror TenantAdminLayout),
 *   drawer < 1024px theo mockup shared/components.css
 * - Dependencies: React Router, useAuthStore, NQH design tokens
 *
 * @cross-references
 * - Mockup: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/hub.html
 * - Convention: frontend/src/layouts/TenantAdminLayout.tsx
 * - Guard roles: backend/app/constants/roles.py TENANT_MANAGER_ROLES_STR
 * ===============================================================================
 */

import React, { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { getDashboardRouteForRole } from "../utils/roleDashboardMap";
import { useManagerWeekSummary } from "../hooks/useManagerHub";
import styles from "./ManagerLayout.module.css";

/**
 * Guard roles — mirror backend TENANT_MANAGER_ROLES_STR
 * (backend/app/constants/roles.py:126-136). Đây là nhóm role "quản lý"
 * (BRANCH_MANAGER / SHIFT_MANAGER=Trưởng ca / HEAD_CHEF=Bếp trưởng /
 * CORPORATE_MANAGER=Quản lý phòng ban / TENANT_OWNER / TENANT_ADMIN).
 */
const MANAGER_ROLES = [
  "TENANT_OWNER",
  "TENANT_ADMIN",
  "CORPORATE_MANAGER",
  "BRANCH_MANAGER",
  "SHIFT_MANAGER",
  "HEAD_CHEF",
];

/** Nhãn vai trò VI verbatim theo mockup hub.html / hub-shift.html sidebar__org-role.
 * TENANT_OWNER (2026-08-28, @ba đánh giá kèm BOT-004 #667): mockup gốc ghi "Chủ
 * sở hữu" — lệch chuẩn `UI-LABELS-VI-ENUM.md` §5 ("Chủ doanh nghiệp", nguồn
 * ProfilePage.tsx, chốt 2026-07-13). 2 mockup này chưa từng đối chiếu SSOT khi
 * bảng chốt. Đổi khớp SSOT — xem UI-LABELS-VI-ENUM.md §5 mục "Drift đã chốt" #4.
 * CORPORATE_MANAGER (2026-08-28, cùng đợt rà soát): mockup gốc ghi "Quản lý
 * vùng" — 3 chỗ khác nhau trong code từng có 3 nhãn khác nhau cho cùng role
 * này (xem Drift #6). CHỐT "Quản lý phòng ban", người yêu cầu xác nhận trực
 * tiếp — supersede cả nhãn cũ "Quản lý chuỗi" từng ghi tạm trong SSOT. */
const ROLE_LABELS: Record<string, string> = {
  BRANCH_MANAGER: "Quản lý chi nhánh",
  SHIFT_MANAGER: "Trưởng ca",
  HEAD_CHEF: "Bếp trưởng",
  CORPORATE_MANAGER: "Quản lý phòng ban",
  TENANT_ADMIN: "Quản trị viên",
  TENANT_OWNER: "Chủ doanh nghiệp",
};

/** Nav chính — nhãn VI verbatim theo mockup sidebar__nav.
 * BUG THẬT đã vá (2026-08-16, theo phản hồi người dùng "nút Giao việc đâu
 * rồi?"): "Giao việc" (route /tasks, TasksProfessional.tsx — SPEC-0418/
 * SPEC-0713 Task Engine, có sẵn từ Sprint 78-79) CHƯA TỪNG được thêm vào
 * đây — mockup gốc hub.html cũng không có (chỉ có Tổng quan/Phân ca/Chấm
 * công/Nhân viên), tính năng Giao việc được xây sau nhưng không ai nối vào
 * sidebar mới này. "Duyệt đơn nghỉ phép" cũng là tiền lệ tương tự (thêm sau
 * mockup, đã có sẵn ở đây) — theo đúng tiền lệ đó. */
const NAV_ITEMS = [
  { path: "/manager/hub", label: "Tổng quan" },
  { path: "/manager/schedule", label: "Phân ca" },
  { path: "/manager/attendance", label: "Chấm công" },
  // 2026-08-16: đổi /tasks → /manager/tasks (route lồng ManagerLayout mới,
  // App.tsx) — theo phản hồi người dùng, /tasks độc lập làm mất sidebar.
  { path: "/manager/tasks", label: "Giao việc" },
  { path: "/manager/staff", label: "Nhân viên" },
  { path: "/manager/leave-approvals", label: "Duyệt đơn nghỉ phép" },
  // DD-0106 (2026-08-30): "Đổi ca" — theo đúng tiền lệ "Duyệt đơn nghỉ phép"
  // ở trên (thêm sau mockup gốc).
  { path: "/manager/shift-swaps", label: "Đổi ca" },
];

const ManagerLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, userRole, tenantKey, logout } = useAuthStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Hook phải gọi TRƯỚC mọi early return (Rules of Hooks). Ngữ cảnh tổ chức
  // THẬT: tên/mã chi nhánh từ week-summary endpoint (Organization.name/code qua
  // DD-0016 branch anchor — ví dụ "BKL"), fallback tenantKey. KHÔNG hardcode.
  const { data: weekSummary } = useManagerWeekSummary();

  // Chờ auth state — tránh flash (mirror RequireRole behavior)
  if (isLoading) return null;

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const normalizedRole = userRole?.toUpperCase() || "";
  if (!MANAGER_ROLES.includes(normalizedRole)) {
    // Không phải nhóm quản lý → về dashboard đúng vai trò (single role→route source)
    return <Navigate to={getDashboardRouteForRole(userRole)} replace />;
  }

  const roleLabel = ROLE_LABELS[normalizedRole] || normalizedRole;
  const orgName = [weekSummary?.branch_name || tenantKey, weekSummary?.branch_code]
    .filter(Boolean)
    .join(" · ") || "NQH-Bot";

  const activeItem = NAV_ITEMS.find(
    (item) =>
      location.pathname === item.path || location.pathname.startsWith(item.path + "/"),
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className={`${styles.appShell} ${drawerOpen ? styles.drawerOpen : ""}`}>
      {/* overlay đóng drawer (mobile/tablet) */}
      <div className={styles.drawerOverlay} onClick={closeDrawer} aria-hidden="true" />

      {/* ── Sidebar 240px (D1) — shell Quản lý ── */}
      <aside className={styles.sidebar} id="manager-sidebar" aria-label="Điều hướng Quản lý">
        <div className={styles.sidebarOrg}>
          <div className={styles.sidebarOrgName}>{orgName}</div>
          <div className={styles.sidebarOrgRole}>{roleLabel}</div>
        </div>
        <nav className={styles.sidebarNav} aria-label="Điều hướng chính">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeDrawer}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.isActive : ""}`
              }
            >
              {item.label}
              <span className={styles.navItemMark} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} width={14} height={14}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </span>
            </NavLink>
          ))}
        </nav>
        <div className={styles.sidebarFoot}>
          {/* [tên Quản lý] → SC-013-MGR-PROFILE */}
          <NavLink
            to="/manager/profile"
            onClick={closeDrawer}
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.isActive : ""}`
            }
          >
            {user.full_name}
          </NavLink>
          {/* Đăng xuất → logout thật từ auth store, về /login (SC-001-LOGIN) */}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnBlock}`}
            onClick={handleLogout}
          >
            Đăng xuất
          </button>
        </div>
      </aside>

      <div className={styles.mainCol}>
        {/* ── Topbar ☰ (chỉ < 1024px) ── */}
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.btnIcon}
            aria-label="Mở menu"
            aria-expanded={drawerOpen}
            aria-controls="manager-sidebar"
            onClick={() => setDrawerOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={24} height={24} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>
          <span>
            {orgName} · {activeItem?.label || "Tổng quan"}
          </span>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ManagerLayout;
